import { Input, Stack, Text, Textarea } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import {
  createBusinessFeature,
  updateBusinessFeature,
} from "../api/company-knowledge";
import { useLoading, useToaster } from "../hooks";
import {
  BusinessFeature,
  CustomerProfileSubset,
} from "../types/company-knowledge";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import { Field } from "./field";
import { toaster } from "./toaster";

// A feature passes `feature` to edit an existing one, or null to
// create. The ICP subsets come from the parent page, which loads them
// alongside the features for its "Applicable ICP subsets" column.

const businessFeatureSchema = z.object({
  name: z.string().trim().min(1, "A name is required."),
  precursor: z.string(),
  "problem-domain": z.string(),
  description: z.string(),
  "feature-url": z
    .string()
    .refine(
      (value) => value === "" || /^https?:\/\//.test(value),
      "Enter a valid URL (starting with http:// or https://), or leave it blank.",
    ),
  "subset-ids": z.array(z.number()),
});

type BusinessFeatureFormData = z.infer<typeof businessFeatureSchema>;

interface BusinessFeatureDialogProps {
  feature?: BusinessFeature | null;
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onSuccess: () => void;
  subsets: CustomerProfileSubset[];
  workspaceSlug: string;
}

export function BusinessFeatureDialog({
  feature,
  isOpen,
  onOpenChange,
  onSuccess,
  subsets,
  workspaceSlug,
}: BusinessFeatureDialogProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<BusinessFeatureFormData>({
    resolver: zodResolver(businessFeatureSchema),
    defaultValues: emptyFeature,
  });

  React.useEffect(() => {
    if (!isOpen) return;
    reset(feature ? toFormData(feature) : emptyFeature);
  }, [feature, isOpen, reset]);

  const onSubmit = async (data: BusinessFeatureFormData) => {
    await withLoading(async () => {
      await withToaster(async () => {
        if (feature) {
          await updateBusinessFeature(workspaceSlug, feature.id, data);
          toaster.create({ type: "success", title: "Feature updated" });
        } else {
          await createBusinessFeature(workspaceSlug, data);
          toaster.create({ type: "success", title: "Feature created" });
        }
        onOpenChange({ open: false });
        onSuccess();
      });
    });
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange} size="md">
      <DialogContent ref={ref}>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>
            {feature ? "Edit Feature" : "Create Feature"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogBody paddingBottom="6">
            <Stack gap="4">
              <Field
                label="Name"
                invalid={!!errors.name}
                errorText={errors.name?.message}
              >
                <Input
                  size="sm"
                  placeholder="The feature's name"
                  {...register("name")}
                />
              </Field>
              <Field
                label="How does the customer do this today?"
                helperText="The current workaround or alternative, before the product."
                invalid={!!errors.precursor}
                errorText={errors.precursor?.message}
              >
                <Textarea
                  {...register("precursor")}
                  placeholder="What they do instead today."
                  rows={2}
                />
              </Field>
              <Field
                label="What problem does this address?"
                invalid={!!errors["problem-domain"]}
                errorText={errors["problem-domain"]?.message}
              >
                <Textarea
                  {...register("problem-domain")}
                  placeholder="The problem the customer has that this feature solves."
                  rows={2}
                />
              </Field>
              <Field
                label="What does your feature change?"
                invalid={!!errors.description}
                errorText={errors.description?.message}
              >
                <Textarea
                  {...register("description")}
                  placeholder="What the feature does for the customer."
                  rows={2}
                />
              </Field>
              <Field
                label="Feature page"
                invalid={!!errors["feature-url"]}
                errorText={errors["feature-url"]?.message}
              >
                <Input
                  {...register("feature-url")}
                  placeholder="https://example.com/features/your-feature"
                />
              </Field>
              {/* Deliberately not a <Field>: a Chakra Field shares its
                  label/input ids with every control inside it, which
                  makes each checkbox's label point at the first
                  checkbox's input. Field is for one control; a group
                  gets plain text. */}
              <Stack gap="1">
                <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                  Applicable ICP subsets
                </Text>
                <Controller
                  name="subset-ids"
                  control={control}
                  render={({ field }) => (
                    <Stack gap="2">
                      {subsets.map((subset) => {
                        const checked = field.value.includes(subset.id);
                        return (
                          <Checkbox
                            key={subset.id}
                            checked={checked}
                            onCheckedChange={(details) => {
                              const next = details.checked
                                ? [...field.value, subset.id]
                                : field.value.filter((id) => id !== subset.id);
                              field.onChange(next);
                            }}
                          >
                            {subset.name}
                          </Checkbox>
                        );
                      })}
                      <Text color="fg.muted" fontSize="sm" fontStyle="italic">
                        Applies to all subsets when none are selected.
                      </Text>
                    </Stack>
                  )}
                />
              </Stack>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange({ open: false })}
            >
              Cancel
            </Button>
            <Button loading={isLoading} type="submit">
              {feature ? "Save Changes" : "Create Feature"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}

const emptyFeature: BusinessFeatureFormData = {
  name: "",
  precursor: "",
  "problem-domain": "",
  description: "",
  "feature-url": "",
  "subset-ids": [],
};

// The schema's nullable fields arrive as null over the wire; empty
// strings are the form's representation of "unset".
const toFormData = (feature: BusinessFeature): BusinessFeatureFormData => ({
  name: feature.name ?? "",
  precursor: feature.precursor ?? "",
  "problem-domain": feature["problem-domain"] ?? "",
  description: feature.description ?? "",
  "feature-url": feature["feature-url"] ?? "",
  "subset-ids": feature["subset-ids"] ?? [],
});
